import { ErrorNotifier } from './error-notifier';
import { UngroupedTrackingRule, UnparsedEventTrigger, CustomEventName } from './ungrouped-tracking-rule.types';

enum TrackingRuleType {
  URL = 'url',
  PATH = 'path',
  QUERY = 'query',
  DOMAIN = 'domain',
  REFERRER = 'referrer',
  TITLE = 'title',
}
enum TrackingRuleComparator {
  EQUALS = 'equals',
  DOES_NOT_EQUAL = 'does_not_equal',
  CONTAINS = 'contains',
  DOES_NOT_CONTAIN = 'does_not_contain',
  MATCH_REGEXP = 'match_regexp',
  MATCH_REGEXP_IGNORE_CASE = 'match_regexp_ignore_case',
  DOES_NOT_MATCH_REGEXP = 'does_not_match_regexp',
  DOES_NOT_MATCH_REGEXP_IGNORE_CASE = 'does_not_match_regexp_ignore_case',
}
type TrackingRuleOperator = 'u' | 'i';
type TrackingRuleValue = string;

interface TrackingRuleCondition {
  type: TrackingRuleType;
  comparator: TrackingRuleComparator;
  operator: TrackingRuleOperator;
  value: TrackingRuleValue;
}

type TrackingRuleTrigger = TrackingRuleCondition[];

const regexpComparators = new Set([
  TrackingRuleComparator.MATCH_REGEXP,
  TrackingRuleComparator.MATCH_REGEXP_IGNORE_CASE,
  TrackingRuleComparator.DOES_NOT_MATCH_REGEXP,
  TrackingRuleComparator.DOES_NOT_MATCH_REGEXP_IGNORE_CASE,
]);
const nonUrlTargetTypes = new Set([TrackingRuleType.DOMAIN, TrackingRuleType.REFERRER, TrackingRuleType.TITLE]);

const encodeConditionValue = ({
  rawValue,
  type,
  comparator,
}: {
  rawValue: string;
  type: string;
  comparator: string;
}) => {
  // ルールへの合致を比較する際の value に正規表現が利用されている場合
  // encodeURI を適用してしまうと | や ^ など 正規表現における特殊文字がエスケープされてしまい
  // 意図通りの比較ができないため、Supplierが入力した文字列（condition.value）をescapeせずvalueとして保持しておく
  if (regexpComparators.has(comparator as TrackingRuleComparator) || nonUrlTargetTypes.has(type as TrackingRuleType)) {
    return rawValue;
  }
  // TODO: 多重エンコードを抑止する仕組みを入れたほうがより安全
  // ref. https://github.com/reproio/web-sdk/pull/281#discussion_r265459759
  return encodeURI(rawValue);
};

// 将来的にもっと複雑な条件指定や、条件同士の集合演算が利用できるようになったら
// トラッキングルール自体の保持と、locationなど状態・環境との条件比較とでクラスを分割するなど、
// クラス構造をあらためて見直すこと
export class TrackingRule {
  public eventName: CustomEventName;
  public triggers: TrackingRuleTrigger[];

  public constructor({ eventName, triggers }: { eventName: CustomEventName; triggers: UnparsedEventTrigger[] }) {
    this.eventName = eventName;
    this.triggers = triggers.map((trigger) => {
      const parsedTrigger = JSON.parse(trigger) as TrackingRuleTrigger;
      return parsedTrigger.map((condition) => ({
        ...condition,
        value: encodeConditionValue({
          rawValue: condition.value,
          type: condition.type,
          comparator: condition.comparator,
        }),
      }));
    });
  }

  public isAnyofTriggersSatisfied(urlObject: Location): boolean {
    return this.triggers.some((trigger) =>
      trigger.every((condition: TrackingRuleCondition) => TrackingRule.isValidCondition(urlObject, condition))
    );
  }

  private static isValidCondition(urlObject: Location, condition: TrackingRuleCondition): boolean {
    let comparisonTarget;

    try {
      switch (condition.type) {
        case TrackingRuleType.URL:
          comparisonTarget = urlObject.href;
          break;
        case TrackingRuleType.PATH:
          comparisonTarget = urlObject.pathname;
          break;
        case TrackingRuleType.QUERY:
          comparisonTarget = urlObject.search.substring(1);
          break;
        case TrackingRuleType.DOMAIN:
          comparisonTarget = document.domain;
          break;
        case TrackingRuleType.REFERRER:
          comparisonTarget = document.referrer;
          break;
        case TrackingRuleType.TITLE:
          comparisonTarget = document.title;
          break;
        default:
          throw new Error(`the given condition type ${condition.type} is invalid!`);
      }

      if (comparisonTarget == null) {
        throw new Error('comparisonTarget is undefined');
      }

      switch (condition.comparator) {
        case TrackingRuleComparator.EQUALS:
          return comparisonTarget === condition.value;
        case TrackingRuleComparator.DOES_NOT_EQUAL:
          return comparisonTarget !== condition.value;
        case TrackingRuleComparator.CONTAINS:
          return comparisonTarget.includes(condition.value);
        case TrackingRuleComparator.DOES_NOT_CONTAIN:
          return !comparisonTarget.includes(condition.value);
        case TrackingRuleComparator.MATCH_REGEXP: {
          const regexp = new RegExp(condition.value);
          return regexp.test(comparisonTarget);
        }
        case TrackingRuleComparator.MATCH_REGEXP_IGNORE_CASE: {
          const regexp = new RegExp(condition.value, 'i');
          return regexp.test(comparisonTarget);
        }
        case TrackingRuleComparator.DOES_NOT_MATCH_REGEXP: {
          const regexp = new RegExp(condition.value);
          return !regexp.test(comparisonTarget);
        }
        case TrackingRuleComparator.DOES_NOT_MATCH_REGEXP_IGNORE_CASE: {
          const regexp = new RegExp(condition.value, 'i');
          return !regexp.test(comparisonTarget);
        }
        default:
          throw new Error(`the given condition comparator ${condition.comparator} is invalid!`);
      }
    } catch (error: any) {
      console.warn(error);

      const keys = (obj: Object | null | undefined) => (obj == null ? obj : Object.keys(obj));

      ErrorNotifier.notify({
        error,
        extraPayload: {
          urlObject,
          condition,
          comparisonTarget,
          location: window.location,
          // document そのものをJSONに含めようとすると再帰的な参照が含まれている場合エラーになるので、個別に列挙
          documentUrl: window.document.URL,
          documentDomain: window.document.domain,
          documentReferrer: window.document.referrer,
          documentTitle: window.document.title,
          document: keys(window.document),
          urlClass: keys(window.URL),
          urlProto: keys(window.URL && window.URL.prototype),
        },
      });
      return false;
    }
  }
}

type GroupedTrackingRules = { [key: string]: UnparsedEventTrigger[] };

export const buildTrackingRules = (ungroupedTrackingRules: UngroupedTrackingRule[]): TrackingRule[] => {
  const groupTrackingRulesByEventName = (
    rulesByEventName: GroupedTrackingRules,
    currentValue: UngroupedTrackingRule
  ): GroupedTrackingRules => {
    const [eventName, conditions] = currentValue;

    if (!rulesByEventName[eventName]) {
      return { ...rulesByEventName, ...{ [eventName]: [conditions] } };
    }
    rulesByEventName[eventName].push(conditions);

    return rulesByEventName;
  };

  const groupedRules: GroupedTrackingRules = ungroupedTrackingRules.reduce<GroupedTrackingRules>(
    groupTrackingRulesByEventName,
    {}
  );

  return Object.keys(groupedRules).map(
    (eventName) =>
      new TrackingRule({
        eventName,
        triggers: groupedRules[eventName],
      })
  );
};
